import React, { useState, useEffect } from "react";
import axios from "../../../../api/axios";
import { mensajeError } from "../../../../utils/mensajesError";
import { validarIdentificacion, validarEmail } from "../../../../utils/validacionesEcuador";
import "./DatosGenerales.css";
import "../ClienteDetalle.pro.css";

export default function DatosGenerales({ cliente, onActualizacion }) {
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({
    identificacion: "",   // 👈 NUEVO
    nombres: "",
    apellidos: "",
    telefono: "",
    email: "",
    direccion: "",
    ciudad: "",
    servicio: "",
    plan: "",
    ficha: "",
    vineta: "",
    ip_asignada: ""  // NUEVO CAMPO
  });
  const [services, setServices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [pools, setPools] = useState([]);
  const [selectedPoolId, setSelectedPoolId] = useState("");
  const [availableIps, setAvailableIps] = useState([]);
  const [loadingIps, setLoadingIps] = useState(false);
  const [procesando, setProcesando] = useState(false);

  // Validación de cédula/RUC y correo: solo se exige lo que se modificó (un dato antiguo que no se toca no bloquea el guardado)
  const errores = {};
  if (showEditModal) {
    const cedulaNueva = (editForm.identificacion || "").trim();
    if (cedulaNueva !== (cliente.identificacion || "").trim()) {
      const r = validarIdentificacion(cedulaNueva, cliente.tipo_identificacion);
      if (!r.ok) errores.identificacion = r.mensaje;
    }
    const correoNuevo = (editForm.email || "").trim();
    if (correoNuevo !== (cliente.email || "").trim()) {
      const r = validarEmail(correoNuevo);
      if (!r.ok) errores.email = r.mensaje;
    }
  }
  const hayErrores = Object.keys(errores).length > 0;

  // Cargar servicios para el modal de edición
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const res = await axios.get("/services?active_only=true");
        setServices(res.data);
      } catch (error) {
        console.error("Error cargando servicios:", error);
      }
    };
    fetchServices();
  }, []);

  // Cargar pools disponibles
  useEffect(() => {
    const fetchPools = async () => {
      try {
        const res = await axios.get("/pools");
        setPools(res.data);
      } catch (error) {
        console.error("Error cargando pools:", error);
      }
    };
    fetchPools();
  }, []);

  // Cargar planes cuando se selecciona un servicio
  useEffect(() => {
    const fetchPlans = async () => {
      if (!selectedServiceId) {
        setPlans([]);
        return;
      }
      try {
        const res = await axios.get(`/services/plans?service_id=${selectedServiceId}&active_only=true`);
        setPlans(res.data);
      } catch (error) {
        console.error("Error cargando planes:", error);
      }
    };
    fetchPlans();
  }, [selectedServiceId]);

  // Cargar IPs disponibles cuando se selecciona un pool
  useEffect(() => {
    const fetchAvailableIps = async () => {
      if (!selectedPoolId) {
        setAvailableIps([]);
        return;
      }
      setLoadingIps(true);
      try {
        const res = await axios.get(`/pools/${selectedPoolId}/free-ips`);
        setAvailableIps(res.data);
      } catch (error) {
        console.error("Error cargando IPs disponibles:", error);
      } finally {
        setLoadingIps(false);
      }
    };
    fetchAvailableIps();
  }, [selectedPoolId]);

  // Abrir modal de edición con los datos actuales
  const abrirModalEdicion = () => {
    setEditForm({
      identificacion: cliente.identificacion || "",   // 👈 NUEVO
      nombres: cliente.nombres || "",
      apellidos: cliente.apellidos || "",
      telefono: cliente.telefono || "",
      email: cliente.email || "",
      direccion: cliente.direccion || "",
      ciudad: cliente.ciudad || "",
      servicio: cliente.servicio || "",
      plan: cliente.plan || "",
      ficha: cliente.ficha || "",
      vineta: cliente.vineta || "",
      ip_asignada: cliente.ip_asignada || ""
    });

    // Seleccionar servicio y plan si existen
    const service = services.find(s => s.service_type === cliente.servicio);
    if (service) {
      setSelectedServiceId(service.id.toString());
    }

    setShowEditModal(true);
  };

  // Guardar cambios del cliente
  const handleGuardarCambios = async () => {
    if (hayErrores) {
      alert("Corrige los datos marcados en rojo antes de guardar");
      return;
    }
    setProcesando(true);
    try {
      const payload = {
        // si la cédula está vacía no se envía: así no se borra la que ya tenía
        ...(editForm.identificacion ? { identificacion: editForm.identificacion } : {}),
        nombres: editForm.nombres,
        apellidos: editForm.apellidos,
        telefono: editForm.telefono || null,
        email: editForm.email || null,
        direccion: editForm.direccion || null,
        ciudad: editForm.ciudad || null,
        servicio: selectedServiceId ? services.find(s => s.id === parseInt(selectedServiceId))?.service_type : editForm.servicio,
        plan: editForm.plan || null,
        ficha: editForm.ficha || null,
        vineta: editForm.vineta || null
      };

      await axios.put(`/customers/${cliente.id}`, payload);

      // Si la IP cambió, actualizar la asignación
      if (editForm.ip_asignada !== cliente.ip_asignada) {
        if (editForm.ip_asignada) {
          // Asignar nueva IP (primero liberar la existente si hay)
          if (cliente.ip_asignada) {
            await axios.delete(`/mikrotik/client-ip/${cliente.id}`);
          }
          // Asignar nueva IP
          const poolSeleccionado = pools.find(p => p.id === parseInt(selectedPoolId));
          await axios.post(`/mikrotik/client-ip`, {
            cliente_id: cliente.id,
            mikrotik_id: poolSeleccionado?.mikrotik_id || 1,
            pool_id: selectedPoolId || null,
            ip_asignada: editForm.ip_asignada
          });
        } else if (cliente.ip_asignada) {
          // Liberar IP existente
          await axios.delete(`/mikrotik/client-ip/${cliente.id}`);
        }
      }

      setShowEditModal(false);
      if (onActualizacion) onActualizacion();
      alert("✅ Cliente actualizado correctamente");
    } catch (error) {
      console.error("Error actualizando cliente:", error);
      alert(mensajeError(error, "Error al actualizar cliente"));
    } finally {
      setProcesando(false);
    }
  };

  // Función para liberar IP directamente sin abrir modal
  const handleLiberarIP = async () => {
    if (!window.confirm(`¿Está seguro de liberar la IP ${cliente.ip_asignada} del cliente ${cliente.nombres} ${cliente.apellidos}?`)) {
      return;
    }

    setProcesando(true);
    try {
      await axios.delete(`/mikrotik/client-ip/${cliente.id}`);
      if (onActualizacion) onActualizacion();
      alert("✅ IP liberada correctamente");
    } catch (error) {
      console.error("Error liberando IP:", error);
      alert(mensajeError(error, "Error al liberar IP"));
    } finally {
      setProcesando(false);
    }
  };

  return (
    <div className="datos-generales dg-pro">
      {/* Header con título y botón de edición */}
      <div className="section-header">
        <div className="header-left">
          <div className="header-icon user-icon">👤</div>
          <div>
            <h2>Datos Generales</h2>
            <p>Información personal y de servicio</p>
          </div>
        </div>
        <div className="header-actions">
          {cliente.ip_asignada && (
            <button onClick={handleLiberarIP} className="btn-liberar-ip" disabled={procesando}>
              🔓 Liberar IP
            </button>
          )}
          <button onClick={abrirModalEdicion} className="btn-edit">
            ✏️ Editar Cliente
          </button>
        </div>
      </div>

      {/* Grid principal de información */}
      <div className="info-grid">
        {/* Columna izquierda - Información Personal */}
        <div className="info-card">
          <div className="card-header">
            <span className="card-icon">👤</span>
            <h3>Información Personal</h3>
          </div>
          <div className="card-body">
            <div className="info-row">
              <div className="info-label">Nombre completo</div>
              <div className="info-value">
                {cliente.nombres} {cliente.apellidos}
                <div className="info-sub">{cliente.tipo_identificacion || 'CEDULA'}: {cliente.identificacion || 'sin registrar'}</div>
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">📞 Teléfono</div>
              <div className="info-value">
                {cliente.telefono || <span className="text-muted">No registrado</span>}
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">✉️ Email</div>
              <div className="info-value">
                {cliente.email || <span className="text-muted">No registrado</span>}
              </div>
            </div>
          </div>
        </div>

        {/* Columna central - Dirección y Ubicación */}
        <div className="info-card">
          <div className="card-header">
            <span className="card-icon">📍</span>
            <h3>Dirección y Ubicación</h3>
          </div>
          <div className="card-body">
            <div className="info-row">
              <div className="info-label">📍 Dirección</div>
              <div className="info-value info-address">
                {cliente.direccion || <span className="text-muted">No registrada</span>}
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">🌍 Ciudad</div>
              <div className="info-value">
                {cliente.ciudad || <span className="text-muted">No registrada</span>}
              </div>
            </div>

            {cliente.latitud && cliente.longitud && (
              <div className="info-row">
                <div className="info-label">📍 Coordenadas</div>
                <div className="info-value coordinates">
                  {cliente.latitud.toFixed(6)}, {cliente.longitud.toFixed(6)}
                  <a className="map-link" target="_blank" rel="noreferrer"
                     href={`https://www.google.com/maps/search/?api=1&query=${cliente.latitud},${cliente.longitud}`}>Ver en el mapa</a>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Columna derecha - Servicio y Estado */}
        <div className="info-card">
          <div className="card-header">
            <span className="card-icon">📦</span>
            <h3>Servicio y Estado</h3>
          </div>
          <div className="card-body">
            <div className="info-row">
              <div className="info-label">📡 Servicio</div>
              <div className="info-value">
                <span className="service-badge">
                  {cliente.servicio === 'INTERNET' && '🌐'}
                  {cliente.servicio === 'TV' && '📺'}
                  {cliente.servicio === 'DUOPACK' && '📦'}
                  {cliente.servicio}
                </span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">📋 Plan</div>
              <div className="info-value">
                {cliente.plan || <span className="text-muted">Sin plan</span>}
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">⚡ Estado</div>
              <div className="info-value">
                <span className={`status-badge status-${cliente.estado?.toLowerCase()}`}>
                  {cliente.estado === 'ACTIVO' && '✅ '}
                  {cliente.estado === 'SUSPENDIDO' && '⏸️ '}
                  {cliente.estado === 'RETIRADO' && '📦 '}
                  {cliente.estado === 'PENDIENTE_INSTALACION' && '🛠️ '}
                  {cliente.estado === 'PENDIENTE_INSTALACION' ? 'PENDIENTE DE INSTALACIÓN' : cliente.estado}
                </span>
              </div>
            </div>

            <div className="info-row">
              <div className="info-label">🌐 IP Asignada</div>
              <div className="info-value">
                {cliente.ip_asignada ? (
                  <span className="ip-badge">{cliente.ip_asignada}</span>
                ) : (
                  <span className="text-muted">No asignada</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sección de Ficha y Viñeta */}
      {(cliente.ficha || cliente.vineta) && (
        <div className="additional-info">
          <div className="card-header">
            <span className="card-icon">📄</span>
            <h3>Información Adicional</h3>
          </div>
          <div className="additional-grid">
            {cliente.vineta && (
              <div className="vineta-card">
                <div className="vineta-icon">📌</div>
                <div>
                  <div className="vineta-label">Viñeta / Nota rápida</div>
                  <div className="vineta-value">{cliente.vineta}</div>
                </div>
              </div>
            )}

            {cliente.ficha && (
              <div className={`ficha-card ${cliente.vineta ? '' : 'full-width'}`}>
                <div className="ficha-icon">📄</div>
                <div className="ficha-content">
                  <div className="ficha-label">Ficha Técnica</div>
                  <div className="ficha-value">{cliente.ficha}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal para editar cliente */}
      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content modal-large">
            {/* Header del modal */}
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">✏️</div>
                <div>
                  <h3>Editar Cliente</h3>
                  <p>Modifica la información del cliente</p>
                </div>
              </div>
              <button onClick={() => setShowEditModal(false)} className="modal-close">
                ✕
              </button>
            </div>

            {/* Cuerpo del modal */}
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Nombres</label>
                  <input
                    type="text"
                    value={editForm.nombres}
                    onChange={(e) => setEditForm({...editForm, nombres: e.target.value.toUpperCase()})}
                    className="form-input uppercase"
                  />
                </div>

                <div className="form-group">
                  <label>Apellidos</label>
                  <input
                    type="text"
                    value={editForm.apellidos}
                    onChange={(e) => setEditForm({...editForm, apellidos: e.target.value.toUpperCase()})}
                    className="form-input uppercase"
                  />
                </div>

                {/* 👇 NUEVO CAMPO: CÉDULA EDITABLE */}
                <div className="form-group">
                  <label>🆔 Cédula / Identificación</label>
                  <input
                    type="text"
                    value={editForm.identificacion}
                    onChange={(e) => setEditForm({...editForm, identificacion: e.target.value.replace(/\s/g, "")})}
                    className={`form-input ${errores.identificacion ? "input-error" : ""}`}
                    placeholder="Cédula (10 dígitos) o RUC (13)"
                    maxLength={20}
                  />
                  {errores.identificacion && <div className="campo-error">{errores.identificacion}</div>}
                </div>

                <div className="form-group">
                  <label>📞 Teléfono</label>
                  <input
                    type="text"
                    value={editForm.telefono}
                    onChange={(e) => setEditForm({...editForm, telefono: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>✉️ Email</label>
                  <input
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({...editForm, email: e.target.value.replace(/\s/g, "")})}
                    className={`form-input ${errores.email ? "input-error" : ""}`}
                    maxLength={120}
                  />
                  {errores.email && <div className="campo-error">{errores.email}</div>}
                </div>

                <div className="form-group full-width">
                  <label>📍 Dirección</label>
                  <input
                    type="text"
                    value={editForm.direccion}
                    onChange={(e) => setEditForm({...editForm, direccion: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>🌍 Ciudad</label>
                  <input
                    type="text"
                    value={editForm.ciudad}
                    onChange={(e) => setEditForm({...editForm, ciudad: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>📡 Servicio</label>
                  <select
                    value={selectedServiceId}
                    onChange={(e) => setSelectedServiceId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Seleccione servicio</option>
                    {services.map(service => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>📋 Plan</label>
                  <select
                    value={editForm.plan}
                    onChange={(e) => setEditForm({...editForm, plan: e.target.value})}
                    className="form-select"
                    disabled={!selectedServiceId}
                  >
                    <option value="">Seleccione plan</option>
                    {plans.map(plan => (
                      <option key={plan.id} value={plan.name}>
                        {plan.name} - ${plan.price}
                        {plan.speed_mbps && ` (${plan.speed_mbps}Mbps)`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sección de IP */}
                <div className="form-group">
                  <label>🌐 Pool IP</label>
                  <select
                    value={selectedPoolId}
                    onChange={(e) => setSelectedPoolId(e.target.value)}
                    className="form-select"
                  >
                    <option value="">Seleccione pool</option>
                    {pools.map(pool => (
                      <option key={pool.id} value={pool.id}>
                        {pool.nombre} - {pool.network}/{pool.mascara}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>🌐 IP Asignada</label>
                  {loadingIps ? (
                    <div className="loading-ips-small">Cargando IPs...</div>
                  ) : (
                    <select
                      value={editForm.ip_asignada}
                      onChange={(e) => setEditForm({...editForm, ip_asignada: e.target.value})}
                      className="form-select"
                      disabled={!selectedPoolId}
                    >
                      <option value="">Sin IP</option>
                      {availableIps.map(ip => (
                        <option key={ip} value={ip}>{ip}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-group full-width">
                  <label>📌 Viñeta / Nota Rápida</label>
                  <input
                    type="text"
                    value={editForm.vineta}
                    onChange={(e) => setEditForm({...editForm, vineta: e.target.value})}
                    className="form-input"
                    placeholder="Nota corta visible para el cliente"
                  />
                </div>

                <div className="form-group full-width">
                  <label>📄 Ficha Técnica</label>
                  <textarea
                    value={editForm.ficha}
                    onChange={(e) => setEditForm({...editForm, ficha: e.target.value})}
                    rows="4"
                    className="form-textarea"
                    placeholder="Información técnica detallada"
                  />
                </div>
              </div>
            </div>

            {/* Footer del modal */}
            <div className="modal-footer">
              <button
                onClick={() => setShowEditModal(false)}
                className="btn-cancel"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarCambios}
                className="btn-save"
                disabled={procesando || hayErrores}
              >
                💾 {procesando ? "Guardando..." : "Guardar Cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
